"use client"

import Image from "next/image"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"

type AuthCharacterPanelProps = {
  characterAvatar?: string | null
  characterName?: string | null
  className?: string
}

export function AuthCharacterPanel({
  characterAvatar,
  characterName,
  className,
}: AuthCharacterPanelProps) {
  const t = useTranslations("auth.characterPanel")

  if (!characterAvatar) return null

  const displayName = characterName ?? t("fallbackName")

  return (
    <div
      className={cn(
        "relative z-10 mx-auto -mt-[4.9rem] h-[14.25rem] w-full overflow-visible [@media(max-height:760px)]:h-[11.15rem] [@media(max-height:560px)]:hidden md:m-0 md:h-auto md:min-h-[440px] md:self-stretch md:overflow-hidden md:border-r md:border-white/[0.08]",
        className
      )}
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute left-1/2 top-2 h-[12.55rem] w-[8.45rem] -translate-x-[calc(50%+1.05rem)] rotate-[-5deg] rounded-[1.45rem] bg-[linear-gradient(145deg,#ff93ad,#1d7df2)] opacity-80 blur-[0.2px] [@media(max-height:760px)]:h-[9.8rem] [@media(max-height:760px)]:w-[6.6rem] [@media(max-height:560px)]:h-[5.85rem] [@media(max-height:560px)]:w-[3.95rem] md:hidden" />
      <div className="pointer-events-none absolute left-1/2 top-7 h-[10.55rem] w-[8.25rem] -translate-x-[calc(50%-1.75rem)] rotate-[8deg] rounded-[1.35rem] border border-white/[0.08] bg-white/[0.035] [@media(max-height:760px)]:h-[8rem] [@media(max-height:760px)]:w-[6.45rem] [@media(max-height:560px)]:top-4 [@media(max-height:560px)]:h-[4.85rem] [@media(max-height:560px)]:w-[3.85rem] md:hidden" />
      <div className="absolute left-1/2 top-0 h-[14.05rem] w-[9.4rem] -translate-x-1/2 overflow-hidden rounded-[1.35rem] border border-accent-blue/60 bg-black shadow-[0_24px_68px_rgba(0,0,0,0.62)] [@media(max-height:760px)]:h-[10.95rem] [@media(max-height:760px)]:w-[7.3rem] [@media(max-height:560px)]:h-[6.25rem] [@media(max-height:560px)]:w-[4.2rem] [@media(max-height:560px)]:rounded-[0.95rem] md:hidden">
        <Image
          src={characterAvatar}
          alt={displayName}
          fill
          sizes="155px"
          className="object-cover object-top"
        />
        <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/55 to-transparent" />
      </div>

      <div className="absolute inset-0 hidden overflow-hidden md:block">
        <Image
          src={characterAvatar}
          alt={displayName}
          fill
          sizes="360px"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-white/45">
            {t("eyebrow")}
          </p>
          <p className="mt-1 text-2xl font-semibold text-white">
            {displayName}
          </p>
        </div>
      </div>
    </div>
  )
}
